import axios from "axios";
import TokenStorage, {
  REFRESH_LOCK_KEY,
  REFRESH_LOCK_TTL_MS,
} from "utils/TokenStorage.js";

let refreshPromise = null;

const refreshClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

refreshClient.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers.Organization = TokenStorage.getOrganization();
  return config;
});

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function waitForUsableAccessToken(timeoutMs = REFRESH_LOCK_TTL_MS) {
  const startedAt = Date.now();
  return new Promise((resolve, reject) => {
    const poll = () => {
      if (TokenStorage.hasUsableAccessToken()) {
        resolve();
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error("Timed out waiting for token refresh"));
        return;
      }
      setTimeout(poll, 100);
    };
    poll();
  });
}

function tryAcquireRefreshLock() {
  const lockId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    const existing = localStorage.getItem(REFRESH_LOCK_KEY);
    if (existing) {
      const existingTs = Number(String(existing).split("-")[0]);
      if (existingTs && Date.now() - existingTs < REFRESH_LOCK_TTL_MS) {
        return null;
      }
    }
    localStorage.setItem(REFRESH_LOCK_KEY, lockId);
  } catch {
    return lockId;
  }
  return lockId;
}

function releaseRefreshLock(lockId) {
  if (!lockId) {
    return;
  }
  try {
    if (localStorage.getItem(REFRESH_LOCK_KEY) === lockId) {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  } catch {
    // Ignore storage failures.
  }
}

async function performRefresh(refreshToken) {
  try {
    const response = await refreshClient.post("/login/refresh", {
      refreshToken,
    });
    const oauthToken = response.data?.data ?? response.data;
    TokenStorage.setTokens(oauthToken);
    return oauthToken;
  } catch (error) {
    // Another tab may have already rotated the refresh token.
    if (TokenStorage.hasUsableAccessToken()) {
      return { access_token: TokenStorage.getAccessToken() };
    }
    throw error;
  }
}

/**
 * Cross-tab-safe access-token refresh.
 * Coordinates SPA tabs and standalone HTML form tabs via a localStorage lock
 * and the shared cookie contract in TokenStorage.
 */
export async function refreshAccessToken() {
  const refreshToken = TokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token");
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  const lockId = tryAcquireRefreshLock();

  refreshPromise = (async () => {
    try {
      if (!lockId) {
        try {
          await waitForUsableAccessToken();
          return { access_token: TokenStorage.getAccessToken() };
        } catch {
          if (TokenStorage.hasUsableAccessToken()) {
            return { access_token: TokenStorage.getAccessToken() };
          }
          return performRefresh(TokenStorage.getRefreshToken() || refreshToken);
        }
      }

      await sleep(40);
      try {
        if (localStorage.getItem(REFRESH_LOCK_KEY) !== lockId) {
          await waitForUsableAccessToken();
          return { access_token: TokenStorage.getAccessToken() };
        }
      } catch {
        // Storage unavailable — proceed with refresh.
      }

      return performRefresh(refreshToken);
    } finally {
      releaseRefreshLock(lockId);
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

export async function ensureValidAccessToken() {
  if (!TokenStorage.shouldRefreshAccessToken()) {
    return;
  }
  await refreshAccessToken();
}
